import { Injectable, inject, signal } from '@angular/core';
import { JitApi } from '../../api/jit-api';
import { HttpService } from '../http/http.service';
import { OperatorId } from '../operator/operator.service';
import { DefaultLightningWallet } from 'src/app/model/wallet/default-wallet';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private httpService = inject(HttpService);
  private idSignal = signal<string | undefined>(undefined);
  private defaultWalletSignal = signal<DefaultLightningWallet | undefined>(undefined);
  private initialisedSignal = signal<boolean | undefined>(undefined);

  /**
   * Getter for user ID.
   * @returns The user ID if set.
   * @throws Error if the user ID is not set.
   */
  get id(): string {
    const id = this.idSignal();
    if (!id) {
      throw new Error('ProfileService: No user set');
    }
    return id;
  }

  /**
   * Gets the default wallet signal.
   * @returns The default wallet signal.
   */
  get defaultWallet() {
    return this.defaultWalletSignal.asReadonly();
  }

  /**
   * Gets the initialised signal.
   * @returns The initialised signal.
   */
  get initialised() {
    return this.initialisedSignal.asReadonly();
  }

  /**
   * Initializes the user profile by fetching the Lightning address and nickname.
   * @param id The user ID.
   * @returns A promise that resolves when initialization is complete.
   */
  async initialise(id: string): Promise<void> {
    this.idSignal.set(id);
    this.initialisedSignal.set(true);
  }

  /**
   * Resets the user profile service state.
   */
  reset(): void {
    this.idSignal.set(undefined);
    this.initialisedSignal.set(undefined);
    this.defaultWalletSignal.set(undefined);
  }

  /**
   * Sets the user's Lightning address.
   * @param lightningAddress The Lightning address to set.
   * @returns A promise that resolves with the API response.
   */
  async setLightningAddress(operatorId: string, lightningAddress: string): Promise<void> {
    this.defaultWalletSignal.set({
      lightningAddress,
    });

    // Don't set lightning address for thndr inhouse
    if (operatorId !== OperatorId.thndr) {
      await JitApi.request(this.httpService).setLightningAddress(lightningAddress);
    }
  }
}
